import {
  memo, useEffect, useLayoutEffect, useRef,
} from '../../lib/teact/teact';
import { getActions, withGlobal } from '../../global';

import type { GlobalState } from '../../global/types';

import { STRICTERDOM_ENABLED } from '../../config';
import { disableStrict, enableStrict } from '../../lib/fasterdom/stricterdom';
import { selectSharedSettings } from '../../global/selectors/sharedState';
import buildClassName from '../../util/buildClassName';
import { reportClientError } from '../../util/clientErrorReporter';
import {
  isWorkGoogleBridgeCallback,
  readWorkGoogleBridgeCallbackTransactionId,
  withPreferredAuthMode,
} from '../../util/googleAuth';
import { oldSetLanguage } from '../../util/oldLangProvider';
import { navigateBack } from './helpers/backNavigation';
import { getSuggestedLanguage } from './helpers/getSuggestedLanguage';
import {
  type AuthQrWatchdogStage,
  getAuthQrWatchdogReport,
} from './authQrWatchdog';

import useAsync from '../../hooks/useAsync';
import useFlag from '../../hooks/useFlag';
import useLang from '../../hooks/useLang';
import useLangString from '../../hooks/useLangString';
import useLastCallback from '../../hooks/useLastCallback';
import useMediaTransitionDeprecated from '../../hooks/useMediaTransitionDeprecated';
import useMultiaccountInfo from '../../hooks/useMultiaccountInfo';

import Button from '../ui/Button';
import Loading from '../ui/Loading';
import AuthGoogle from './AuthGoogle';

import blankUrl from '../../assets/blank.png';

type StateProps = {
  auth: GlobalState['auth'];
  language?: string;
};

const DATA_PREFIX = 'tg://login?token=';
const QR_SIZE = 280;
const QR_PLANE_SIZE = 54;
const QR_CODE_MUTATION_DURATION = 50; // The library is asynchronous and we need to wait for its mutation code
const WORK_GOOGLE_BRIDGE_PHONE_MARKER = 'runai.work_google_bridge.phone_selected';

let qrCodeStylingPromise: Promise<typeof import('qr-code-styling')> | undefined;

function ensureQrCodeStyling() {
  if (!qrCodeStylingPromise) {
    qrCodeStylingPromise = import('qr-code-styling');
  }
  return qrCodeStylingPromise;
}

// Preload immediately when this module is imported, so the library
// is ready by the time the QR token arrives from the server.
ensureQrCodeStyling();

const AuthCode = ({
  auth,
  language,
}: StateProps) => {
  const {
    returnToAuthPhoneNumber,
    setSharedSettingOption,
    loginWithPasskey,
  } = getActions();

  const { state, qrCode: authQrCode, passkeyOption } = auth;

  const suggestedLanguage = getSuggestedLanguage();
  const lang = useLang();
  const qrCodeRef = useRef<HTMLDivElement>();

  const continueText = useLangString('AuthContinueOnThisLanguage', suggestedLanguage);
  const [isLoading, markIsLoading, unmarkIsLoading] = useFlag();
  const [isQrMounted, markQrMounted, unmarkQrMounted] = useFlag();
  const qrWatchStartAtRef = useRef<number>();
  const qrWatchReportedStageRef = useRef<AuthQrWatchdogStage>('none');

  const accountsInfo = useMultiaccountInfo();
  const hasActiveAccount = Object.values(accountsInfo).length > 0;

  const { result: qrCode } = useAsync(async () => {
    const QrCodeStyling = (await ensureQrCodeStyling()).default;
    return new QrCodeStyling({
      width: QR_SIZE,
      height: QR_SIZE,
      image: blankUrl,
      margin: 10,
      type: 'svg',
      dotsOptions: {
        type: 'rounded',
      },
      cornersSquareOptions: {
        type: 'extra-rounded',
      },
      imageOptions: {
        imageSize: 0.4,
        margin: 8,
      },
      qrOptions: {
        errorCorrectionLevel: 'M',
      },
    });
  }, []);

  const transitionClassNames = useMediaTransitionDeprecated(isQrMounted);

  useLayoutEffect(() => {
    if (!authQrCode || !qrCode) {
      return () => {
        unmarkQrMounted();
      };
    }

    const container = qrCodeRef.current!;
    const data = `${DATA_PREFIX}${authQrCode.token}`;

    if (STRICTERDOM_ENABLED) {
      disableStrict();
    }

    qrCode.update({
      data,
    });

    if (!isQrMounted) {
      qrCode.append(container);
      markQrMounted();
    }

    if (STRICTERDOM_ENABLED) {
      setTimeout(() => {
        enableStrict();
      }, QR_CODE_MUTATION_DURATION);
    }

    return undefined;
  }, [authQrCode, isQrMounted, qrCode]);

  useEffect(() => {
    const shouldWatchQr = state === 'authorizationStateWaitQrCode' && !isQrMounted;

    if (!shouldWatchQr) {
      qrWatchStartAtRef.current = undefined;
      qrWatchReportedStageRef.current = 'none';
      return undefined;
    }

    qrWatchStartAtRef.current ||= Date.now();

    const interval = window.setInterval(() => {
      const startedAt = qrWatchStartAtRef.current;
      if (!startedAt) return;

      const report = getAuthQrWatchdogReport({
        authState: state,
        hasAuthQrToken: Boolean(authQrCode),
        isQrMounted,
        elapsedMs: Date.now() - startedAt,
        reportedStage: qrWatchReportedStageRef.current || 'none',
      });
      if (!report) return;

      qrWatchReportedStageRef.current = report.stage;
      reportClientError({
        kind: 'perf',
        mode: 'immediate',
        message: `[auth-qr] ${report.stage} timeout after ${report.elapsedMs}ms`,
        metadata: {
          source: 'auth-qr',
          type: 'qr-timeout',
          stage: report.stage,
          elapsedMs: report.elapsedMs,
          authState: report.authState,
          hasAuthQrToken: report.hasAuthQrToken,
          isQrMounted: report.isQrMounted,
        },
        dedupeKey: `auth-qr-timeout:${report.stage}:${Boolean(authQrCode)}`,
      });
    }, 1_000);

    return () => {
      window.clearInterval(interval);
    };
  }, [authQrCode, isQrMounted, state]);

  const handleBackNavigation = useLastCallback(() => {
    navigateBack();
  });

  const handleLangChange = useLastCallback(() => {
    markIsLoading();

    void oldSetLanguage(suggestedLanguage, () => {
      unmarkIsLoading();

      setSharedSettingOption({ language: suggestedLanguage });
    });
  });

  const handleReturnToAuthPhoneNumber = useLastCallback(() => {
    try {
      const bridgeTransactionId = readWorkGoogleBridgeCallbackTransactionId(
        window.location.search,
        window.location.pathname,
        window.location.hostname,
      );
      if (bridgeTransactionId && isWorkGoogleBridgeCallback(window.location.search, window.location.pathname, window.location.hostname)) {
        window.sessionStorage.setItem(WORK_GOOGLE_BRIDGE_PHONE_MARKER, bridgeTransactionId);
      }
    } catch {
      // ignore storage failures
    }
    window.history.replaceState({}, '', withPreferredAuthMode(window.location.href));
    returnToAuthPhoneNumber();
  });

  const handleLoginWithPasskey = useLastCallback(() => {
    loginWithPasskey();
  });

  const shouldShowPhoneLoginButton = state !== 'authorizationStateReady';

  return (
    <div id="auth-qr-form" className="custom-scroll">
      {hasActiveAccount && (
        <Button
          size="smaller"
          round
          color="translucent"
          className="auth-close"
          iconName="close"
          onClick={handleBackNavigation}
        />
      )}
      <div className="auth-form qr">
        <div className="qr-outer">
          <div
            className={buildClassName('qr-inner', transitionClassNames)}
            key="qr-inner"
          >
            <div
              key="qr-container"
              className="qr-container"
              ref={qrCodeRef}
              style={`width: ${QR_SIZE}px; height: ${QR_SIZE}px`}
            />
            <div
              className="qr-plane"
              style={`
                width:${QR_PLANE_SIZE}px;
                height:${QR_PLANE_SIZE}px;
                border-radius:12px;
                background:#2D2926;
                display:flex;
                align-items:center;
                justify-content:center
              `}
            >
              <svg
                width="36"
                height="36"
                viewBox="0 0 16 16"
                xmlns="http://www.w3.org/2000/svg"
              >
                <text
                  x="8"
                  y="12.5"
                  text-anchor="middle"
                  font-family="Arial,Helvetica,sans-serif"
                  font-weight="bold"
                  font-size="12"
                  fill="#fff"
                >
                  R
                </text>
              </svg>
            </div>
          </div>
          {!isQrMounted && <div className="qr-loading"><Loading /></div>}
        </div>
        <h1>{lang('LoginQRTitle')}</h1>
        <ol>
          <li><span>{lang('LoginQRHelp1')}</span></li>
          <li><span>{lang('LoginQRHelp2', undefined, { withNodes: true, withMarkdown: true })}</span></li>
          <li><span>{lang('LoginQRHelp3')}</span></li>
        </ol>
        {shouldShowPhoneLoginButton && (
          <Button className="auth-button" isText onClick={handleReturnToAuthPhoneNumber}>
            {lang('LoginQRCancel')}
          </Button>
        )}
        <AuthGoogle autoPrompt={false} />
        {passkeyOption && (
          <Button className="auth-button" isText onClick={handleLoginWithPasskey}>
            {lang('LoginPasskey')}
          </Button>
        )}
        {suggestedLanguage && suggestedLanguage !== language && continueText && (
          <Button className="auth-button" isText isLoading={isLoading} onClick={handleLangChange}>
            {continueText}
          </Button>
        )}
      </div>
    </div>
  );
};

export default memo(withGlobal(
  (global): Complete<StateProps> => {
    const { auth } = global;
    const { language } = selectSharedSettings(global);

    return {
      auth,
      language,
    };
  },
)(AuthCode));
